<template>
  <v-card class="affiliate-link-generating-wrapper-block">
    <v-card-title class="affiliate-link-generating-title">
      <span>
        {{ $t('affiliateProgram.links.generateLink') }}
      </span>
      <v-tooltip
        top
        class="affiliate-link-generating-tooltip"
      >
        <template #activator="{ on }">
          <v-btn
            class="affiliate-link-generating-question-btn"
            icon
            v-on="on"
          >
            <question-outline-icon class="affiliate-link-generating-question-icon" />
          </v-btn>
        </template>
        <div
          v-for="(text, index) in linkGeneratingInfoTexts"
          :key="index"
          class="affiliate-link-generating-tooltip-text"
        >
         {{ text }}
        </div>
      </v-tooltip>
    </v-card-title>
    <v-card-text class="affiliate-link-generating-content">
      <v-text-field
        v-model="affiliateLink"
        outlined
        dense
        :hide-details="!linkErrorMessage"
        class="affiliate-link-generating-text-field"
        :error-messages="linkErrorMessage"
      >
        <template #message="props">
          <div class="affiliate-link-generating-text-field-error-wrapper">
            <close-outline class="affiliate-link-generating-text-field-icon" />
            <span class="affiliate-link-generating-text-field-err-text">
              {{ $t(`affiliateProgram.links.${props.message}`) }}
            </span>
          </div>
        </template>

        <template #prepend-inner>
          <span class="affiliate-link-generating-text-field-main-link">{{ mainLinkSource }}</span>
        </template>

        <template #append>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                width="24"
                height="24"
                icon
                class="affiliate-link-generating-reload-btn"
                v-on="on"
                @click="generateLink"
              >
                <reload-outline-icon class="affiliate-link-generating-reload-icon" />
              </v-btn>
            </template>

            <span class="affiliate-link-generating-tooltip-text">
              {{ $t('affiliateProgram.links.randomLinkGeneration') }}
            </span>
          </v-tooltip>
        </template>
      </v-text-field>
      <v-btn
        class="affiliate-link-generating-button"
        color="primary"
        @click="handleGenerateLinkRequest"
      >
        <span>
          {{ $t('affiliateProgram.links.generateLink') }}
        </span>
      </v-btn>
    </v-card-text>
  </v-card>
</template>

<script>
import QuestionOutlineIcon from '@/assets/svgIcons/questionOutline.svg?inline'
import ReloadOutlineIcon from '@/assets/svgIcons/reload.svg?inline'
import CloseOutline from '@/assets/svgIcons/closeOutline.svg?inline'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AffiliateLinkGenerating',
  components: {
    QuestionOutlineIcon,
    ReloadOutlineIcon,
    CloseOutline
  },
  computed: {
    ...mapGetters({
      profile: 'main/profile'
    }),
    mainLinkSource () {
      return `${location.origin}/a/${this.profile.id}/`
    },
    linkGeneratingInfoTexts () {
      const text = this.$t('affiliateProgram.links.generatedLinkWarningText')
      return text?.split("\\n")
    },
    linkErrorMessage () {
      if (!this.affiliateLink) {
        return ''
      }

      if (this.affiliateLink.length > 150) {
        return  'charactersMaxNumberWarning'
      }

      if (!/^[A-Za-z0-9_-]+$/.test(this.affiliateLink)) {
        return  'invalidCharactersEntered'
      }

      return ''
    }
  },
  data() {
    return {
      affiliateLink: '',
    };
  },
  created() {
    this.generateLink()
  },
  methods: {
    ...mapActions({
      getCreatingLinks: 'affiliate/getCreatingLinks',
    }),
    generateLink() {
      const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let link = '';

      for (let i = 0; i < 7; i++) {
        const charSet = i < 5 ? characters.slice(0, 52) : characters.slice(52);
        link += charSet.charAt(Math.floor(Math.random() * charSet.length));
      }

      this.affiliateLink = link;
    },
    async handleGenerateLinkRequest() {
      if (!this.linkErrorMessage && this.affiliateLink) {
        await this.handleGenerateAffiliateLink()
      }
    },
    async handleGenerateAffiliateLink () {
      try {
        await this.api.post('/profile/referral-links', { source: this.affiliateLink })
        this.getCreatingLinks()
        this.generateLink()
      } catch (err) {
        console.error(err)
      }
    }
  },
};
</script>

<style lang="scss">
.affiliate-link-generating-wrapper-block,
.affiliate-link-generating-wrapper-block.v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.affiliate-link-generating-title {
  padding: 0;
  color: var(--v-input-label-default-base);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.affiliate-link-generating-content {
  display: flex;
  align-items: center;
  padding: 0;
  column-gap: 16px;
}

.affiliate-link-generating-text-field.v-text-field--outlined fieldset {
  border: none;
  display: none;
}

.affiliate-link-generating-text-field.v-text-field--outlined .v-input__slot,
.affiliate-link-generating-text-field.v-text-field--outlined.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded) > .v-input__control > .v-input__slot {
  display: flex;
  height: 48px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  padding: 0 16px;
  border: 1px solid var(--v-input-border-focused-base);
}

.affiliate-link-generating-text-field.v-text-field--outlined.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded).error--text > .v-input__control > .v-input__slot {
  border: 1px solid var(--v-input-border-error-base);

  .v-text-field__slot input {
    color: var(--v-input-foreground-error-base);
  }
}

.affiliate-link-generating-text-field {
  overflow: hidden;
  color: var(--v-input-foreground-typed-base);
  text-overflow: ellipsis;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  overflow: clip;

  &.v-text-field.v-text-field--enclosed .v-text-field__details {
    margin: 0;
    padding: 0;
  }
}

.affiliate-link-generating-text-field input {
  display: flex;
  text-overflow: ellipsis;
}

.affiliate-link-generating-button,
.affiliate-link-generating-button:not(.v-btn--round).v-size--default,
.affiliate-link-generating-button.v-btn--is-elevated {
  box-shadow: unset;
  display: flex;
  height: 48px;
  padding: 0 32px;
  justify-content: center;
  align-items: center;
  align-self: start;
  gap: 8px;
  border-radius: 8px;
  background: var(--v-button-primary-background-default-base);
}

.affiliate-link-generating-button span {
  color: var(--v-button-primary-foreground-default-base);
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  text-transform: none;
  letter-spacing: 0;
}

.affiliate-link-generating-text-field.v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined .v-input__append-inner,
.affiliate-link-generating-text-field.v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined .v-input__prepend-inner {
  margin: auto 0;
  padding: 0;
}

.affiliate-link-generating-text-field-main-link {
  color: var(--v-input-foreground-placeholder-base);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin-right: 8px;
  text-wrap: nowrap;
}

.affiliate-link-generating-question-btn,
.affiliate-link-generating-question-btn.v-btn--icon.v-size--default {
  max-width: max-content;
  height: max-content;
  margin-left: 6px;
}

.affiliate-link-generating-question-icon {
  color: #CECDD1;
}

.affiliate-link-generating-reload-btn {
  margin-left: 8px;
}

.affiliate-link-generating-reload-icon {
  width: 16px;
  height: 16px;
  color: #19181A;
}

.affiliate-link-generating-text-field-error-wrapper {
  display: flex;
  align-items: center;
  margin-top: 6px;
}

.affiliate-link-generating-text-field-icon {
  color: var(--v-icon-error-base);
  width: 16px;
  height: 16px;
  margin-right: 8px;
}

.affiliate-link-generating-text-field-err-text {
  color: var(--v-text-error-base);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
</style>
